import React, { ReactNode, useCallback, useEffect, useState } from "react";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Button from "@mui/material/Button";
import FormHelperText from "@mui/material/FormHelperText";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import { SxProps, useTheme } from "@mui/material/styles";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import { useDebounce } from "hooks";

import { isDateRangeStringValid, isDateStringValid } from "utils/common";

import MoreRangeDropdown from "./more-range-dropdown.component";
import parseFixedDateRange, {
  NamedDateRange,
  isDateBeforeUnixMinDate
} from "./utils";

const DEFAULT_DATE_INTERVAL_OPTIONS = [
  <ToggleButton value="today" key="today">
    Today
  </ToggleButton>,
  <ToggleButton value="week" key="week">
    WTD
  </ToggleButton>,
  <ToggleButton value="month" key="month">
    MTD
  </ToggleButton>,
  <ToggleButton value="quarter" key="quarter">
    QTD
  </ToggleButton>,
  <ToggleButton value="year" key="year">
    YTD
  </ToggleButton>
];

type DateRangeFilterProps = {
  onChange?: (
    fromDate: Date | null,
    toDate: Date | null,
    selectedDateRange?: NamedDateRange | null
  ) => void;
  fromDate?: Date | null;
  toDate?: Date | null;
  selectedRange?: NamedDateRange;
  customDateIntervalOptions?: JSX.Element[];
  defaultDateIntervalOptions?: JSX.Element[];
  title?: string;
  label?: ReactNode;
  hideOptions?: boolean;
  hideMoreOptions?: boolean;
  hideDateRange?: boolean;
  dateRangeSx?: SxProps;
  sx?: SxProps;
  sxInputField?: SxProps;
  allowDebounce?: boolean;
  afterChange?: () => void;
  clearable?: boolean;
  actionButton?: ReactNode;
  required?: boolean;
};
export default function DateRangeFilter({
  fromDate = null,
  toDate = null,
  selectedRange = "today",
  onChange,
  title,
  label = "Date Range",
  customDateIntervalOptions,
  defaultDateIntervalOptions = DEFAULT_DATE_INTERVAL_OPTIONS,
  hideOptions = false,
  hideMoreOptions = false,
  hideDateRange = false,
  allowDebounce = false,
  clearable = false,
  sx,
  dateRangeSx,
  sxInputField,
  afterChange,
  actionButton,
  required = false
}: DateRangeFilterProps) {
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<any>(null);
  const [isValid, setIsValid] = useState(true);
  const [lastDaysAgo, setLastDaysAgo] = useState("");
  const [currentFromDate, setCurrentFromDate] = useState<Date | null>(fromDate);
  const [currentToDate, setCurrentToDate] = useState<Date | null>(toDate);
  const [currentSelectedRange, setCurrentSelectedRange] =
    useState(selectedRange);

  const { value: dCurrentFromDate } = useDebounce(currentFromDate);
  const { value: dCurrentToDate } = useDebounce(currentToDate);

  const triggerOnChangeCallback = useCallback(
    (
      from: Date | null,
      to: Date | null,
      range: typeof currentSelectedRange
    ) => {
      if (!from || !to) {
        setIsValid(true);
        onChange && onChange(from, to, range);
      } else {
        const isValid = isDateRangeStringValid(from, to);
        setIsValid(isValid);
        onChange && isValid && onChange(from, to, range);
      }
    },
    [onChange]
  );

  useEffect(() => {
    if (!allowDebounce) return;
    triggerOnChangeCallback(
      dCurrentFromDate,
      dCurrentToDate,
      currentSelectedRange
    );
  }, [
    allowDebounce,
    currentSelectedRange,
    dCurrentFromDate,
    dCurrentToDate,
    triggerOnChangeCallback
  ]);

  useEffect(() => {
    if (allowDebounce) return;
    triggerOnChangeCallback(
      currentFromDate,
      currentToDate,
      currentSelectedRange
    );
  }, [
    allowDebounce,
    currentFromDate,
    currentSelectedRange,
    currentToDate,
    triggerOnChangeCallback
  ]);

  const handleChange = (
    event: React.MouseEvent<HTMLElement> | undefined,
    newRange: NamedDateRange
  ) => {
    setOpen(false);
    if (newRange === null) return;
    setCurrentSelectedRange(newRange);
  };

  useEffect(() => {
    if (lastDaysAgo && currentSelectedRange === "lastDaysAgo") {
      const { dateFrom, dateTo } = parseFixedDateRange(
        currentSelectedRange,
        lastDaysAgo
      );
      if (dateFrom || dateTo) {
        setCurrentFromDate(dateFrom);
        setCurrentToDate(dateTo);
      }
    } else {
      const { dateFrom, dateTo } = parseFixedDateRange(currentSelectedRange);

      if (dateFrom || dateTo) {
        setCurrentFromDate(dateFrom);
        setCurrentToDate(dateTo);
      }
      setLastDaysAgo("");
    }
  }, [currentSelectedRange, setCurrentToDate, setCurrentFromDate, lastDaysAgo]);

  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  return (
    <Stack
      sx={{
        minWidth: 900,
        alignItems: "flex-end",
        columnGap: "2px",
        ...sx
      }}
      gap={0.5}
      direction="row"
      spacing={0}
      data-testid="date-range-filter"
      flexWrap="wrap"
    >
      {!hideDateRange && (
        <Stack direction="column" sx={{ alignItems: "flex-start", mx: 1 }}>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            sx={{
              alignItems: "center",
              ...dateRangeSx
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DesktopDatePicker
                views={["year", "month", "day"]}
                label={label}
                value={currentFromDate}
                onChange={(newValue) => {
                  if (isDateStringValid(newValue) || !newValue) {
                    //do not set date less than UNIX min date which is 01/01/1970, so this kind of dates will not trigger backend request: 01/01/0002, 01/01/0020 etc
                    if (
                      newValue &&
                      allowDebounce &&
                      isDateBeforeUnixMinDate(newValue)
                    ) {
                      return;
                    }
                    setCurrentFromDate(newValue);
                    setCurrentSelectedRange("");
                  }
                  afterChange?.();
                }}
                slotProps={{
                  field: { clearable: clearable },
                  textField: {
                    name: "fromDate",
                    size: "small",
                    sx: {
                      backgroundColor: theme.palette.common.white,
                      minWidth: "150px",
                      ...sxInputField
                    },
                    error: !isValid
                  }
                }}
              />
            </LocalizationProvider>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DesktopDatePicker
                views={["year", "month", "day"]}
                label="to"
                value={currentToDate}
                onChange={(newValue) => {
                  if (isDateStringValid(newValue) || !newValue) {
                    //do not set date less than UNIX min date which is 01/01/1970, so this kind of dates will not trigger backend request: 01/01/0002, 01/01/0020 etc
                    if (
                      newValue &&
                      allowDebounce &&
                      isDateBeforeUnixMinDate(newValue)
                    ) {
                      return;
                    }
                    setCurrentToDate(newValue);
                    setCurrentSelectedRange("");
                  }
                }}
                slotProps={{
                  field: { clearable: clearable },
                  textField: {
                    name: "toDate",
                    size: "small",
                    sx: {
                      backgroundColor: theme.palette.common.white,
                      minWidth: "160px",
                      ...sxInputField
                    },
                    error: !isValid
                  }
                }}
              />
            </LocalizationProvider>
          </Stack>
          {/* Always-on helper text is safe because dashboard callers initialize
              dates to defaults (e.g. today/tomorrow in useDashboard). If a future
              caller opts in via `required` without setting defaults, the message
              will appear on first paint — revisit UX in that case. */}
          {required && (!currentFromDate || !currentToDate) && (
            <FormHelperText sx={{ mt: 0.5 }}>
              Both From and To dates are required
            </FormHelperText>
          )}
        </Stack>
      )}

      {!hideOptions && (
        <Stack direction="row" alignItems="center" px={1}>
          {title && (
            <Typography sx={{ color: grey[700], mr: 1 }}>{title}</Typography>
          )}

          <ToggleButtonGroup
            color="primary"
            value={currentSelectedRange}
            exclusive
            onChange={handleChange}
            aria-label="fixed-range"
            size="small"
            sx={{
              backgroundColor: theme.palette.common.white,
              flexWrap: "wrap"
            }}
          >
            {customDateIntervalOptions && customDateIntervalOptions.length > 0
              ? customDateIntervalOptions
              : defaultDateIntervalOptions}
            {!hideMoreOptions && (
              <Button
                ref={anchorRef}
                size="small"
                onClick={() => {
                  setOpen((prevOpen) => !prevOpen);
                }}
              >
                <ArrowDropDownIcon /> More
              </Button>
            )}
            {actionButton}
            <MoreRangeDropdown
              open={open}
              anchorEl={anchorRef.current}
              lastDaysAgo={lastDaysAgo}
              onLastDaysChanged={(value) => {
                setCurrentSelectedRange("lastDaysAgo");
                setLastDaysAgo(value);
              }}
              handleClose={handleClose}
              currentSelectedRange={currentSelectedRange}
              includeDateIntervalOptions={
                customDateIntervalOptions &&
                customDateIntervalOptions.length > 0
              }
              onChange={handleChange}
            />
          </ToggleButtonGroup>
        </Stack>
      )}
    </Stack>
  );
}
